import React from 'react';
import { ToggleSwitch } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { MdOutlineUpdate, MdEmail, MdPhone, MdBadge, MdGroup, MdLocationOn, MdBusinessCenter, MdCalendarToday, MdFingerprint, MdOpenInNew } from 'react-icons/md';
import { FaCheckCircle, FaTimesCircle, FaBuilding } from 'react-icons/fa';
import { formatDate } from 'helpers/utils';
import { getInitials, getAvatarColor } from 'components/common/select/EmployeeBadge';
import ManagerLink from './ManagerLink';
// Shared with the Employee tab on the User detail screen so both render the
// same record identically.
import { Val, InfoRow, SectionPanel, Card, Toggle, GenderRow } from './employeeInfoPrimitives';

// ── EmployeeDetailInfo ────────────────────────────────────────────────────────

const EmployeeDetailInfo = ({ employee = {} }) => {
  const safe   = (val, fallback = null) => (val || val === 0 ? val : fallback);
  const hr     = employee.hr || {};
  const avatarBg = getAvatarColor(employee.name);

  return (
    <>
      {/* ── Identity card ─────────────────────────────────────────────────── */}
      <Card>
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-slate-100">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div
              className="tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-text-white tw-font-bold tw-text-lg tw-select-none"
              style={{ width: 52, height: 52, background: avatarBg, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            >
              {getInitials(employee.name)}
            </div>
            <div>
              <h5 className="tw-text-base tw-font-semibold tw-text-slate-800 tw-m-0 tw-mb-1">
                Employee ID:{' '}
                <span className="tw-text-primary-600">#{safe(employee.id, '—')}</span>
              </h5>
              <div className="tw-flex tw-items-center tw-gap-3">
                {employee.active ? (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-700 tw-text-xs tw-font-semibold">
                    <FaCheckCircle size={10} /> Activated
                  </span>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-red-50 tw-text-red-600 tw-text-xs tw-font-semibold">
                    <FaTimesCircle size={10} /> Not active
                  </span>
                )}
                <span className="tw-text-xs tw-text-slate-400">
                  Created: {formatDate(employee.created_at) || safe(employee.created_at, '—')}
                </span>
              </div>
            </div>
          </div>
          <div className="tw-text-right tw-text-xs tw-text-slate-400 tw-space-y-0.5">
            <div>Created by: <span className="tw-text-primary-600 tw-font-semibold">{safe(employee.created_by, '—')}</span></div>
            <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
              <MdOutlineUpdate size={13} />
              Updated: <span className="tw-text-primary-600 tw-font-semibold">
                {formatDate(employee.updated_at) || safe(employee.updated_at, '—')}
              </span>
            </div>
          </div>
        </div>

        {/* Basic info grid */}
        <div className="tw-px-5 tw-py-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-8">
          <div className="tw-divide-y tw-divide-slate-50">
            <InfoRow label="Name">
              {employee.portal_user?.id ? (
                // portal_user, NOT employees.user_id — that column holds whoever
                // CREATED the record, so linking by it opened the wrong person.
                // Same new-tab behaviour as the header button: following it should
                // never cost you the record you are reading.
                <Link
                  to={`/authorization/users/${employee.portal_user.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open the portal account in a new tab"
                  className="tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1"
                >
                  {safe(employee.name, 'N/A')}
                  <MdOpenInNew size={11} className="tw-opacity-60" />
                </Link>
              ) : (
                <Val>{safe(employee.name)}</Val>
              )}
            </InfoRow>
            <InfoRow label="Last name"><Val>{safe(employee.last_name)}</Val></InfoRow>
            <InfoRow label="First name"><Val>{safe(employee.first_name)}</Val></InfoRow>
            <InfoRow label="Full name"><Val>{safe(employee.full_name)}</Val></InfoRow>
            <InfoRow label="Title"><Val>{safe(employee.title)}</Val></InfoRow>
            <InfoRow label="Email">
              {employee.email ? (
                <a href={`mailto:${employee.email}`} className="tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline tw-flex tw-items-center tw-gap-1">
                  <MdEmail size={13} />{employee.email}
                </a>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Gender"><GenderRow gender={employee.gender} /></InfoRow>
          </div>

          <div className="tw-divide-y tw-divide-slate-50">
            <InfoRow label="Phone">
              {employee.phone_number ? (
                <a href={`tel:${employee.phone_number}`} className="tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline tw-flex tw-items-center tw-gap-1">
                  <MdPhone size={13} />{employee.phone_number}
                </a>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Manager">
              {/* manager (detail payload) carries full_name/email/title/site for
                  the hover card; parent is the bare {id,name} from the list
                  serializer and is the fallback when only that is present. */}
              <ManagerLink manager={employee.manager || employee.parent} />
            </InfoRow>
            <InfoRow label="Sales team">
              <Toggle checked={!!employee.is_sales} label={employee.is_sales ? 'Member' : 'Not member'} />
            </InfoRow>
            <InfoRow label="Active">
              <Toggle checked={!!employee.active} label={employee.active ? 'Active' : 'Inactive'} />
            </InfoRow>
          </div>
        </div>
      </Card>

      {/* ── Details card ──────────────────────────────────────────────────── */}
      <Card>
        <div className="tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-100 tw-bg-slate-50/60">
          <h5 className="tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0">Details</h5>
        </div>

        <div className="tw-px-5 tw-py-5 tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-5">

          {/* Department */}
          <SectionPanel
            icon={<FaBuilding size={12} />}
            title="Department"
            accentColor="tw-border-l-sky-400"
            headerBg="tw-bg-sky-50"
            iconColor="tw-text-sky-500"
          >
            <InfoRow label="Department">
              {employee.department?.name ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-bg-sky-50 tw-text-sky-700 tw-text-xs tw-font-semibold tw-border tw-border-sky-100">
                  {employee.department.name}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Site office">
              {employee.site?.name ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-sky-600">
                  <MdLocationOn size={13} className="tw-shrink-0" />
                  {employee.site.name}
                </span>
              ) : <Val />}
            </InfoRow>
          </SectionPanel>

          {/* HR */}
          <SectionPanel
            icon={<MdBadge size={14} />}
            title="HR"
            accentColor="tw-border-l-primary-400"
            headerBg="tw-bg-primary-50"
            iconColor="tw-text-primary-500"
          >
            <InfoRow label="Start date">
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-primary-600">
                <MdCalendarToday size={12} className="tw-shrink-0" />
                {formatDate(employee.start_date) || <Val>{safe(employee.start_date)}</Val>}
              </span>
            </InfoRow>
            <InfoRow label="HR code">
              {employee.hr_code ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-primary-50 tw-text-primary-700 tw-text-xs tw-font-bold tw-font-mono tw-border tw-border-primary-100">
                  <MdFingerprint size={11} />{employee.hr_code}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Birthday"><Val>{formatDate(employee.birthday) || safe(employee.birthday)}</Val></InfoRow>
            <InfoRow label="Staff policy">
              {employee.staff_policy ? (
                <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-violet-50 tw-text-violet-700 tw-text-xs tw-font-semibold tw-border tw-border-violet-100">
                  {employee.staff_policy === 'SALES' ? 'Sales-Office' : employee.staff_policy}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Position group"><Val>{safe(employee.position_group?.name)}</Val></InfoRow>
            <InfoRow label="Updated at">
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-slate-500">
                <MdOutlineUpdate size={13} className="tw-text-primary-400" />
                {formatDate(employee.updated_at) || safe(employee.updated_at, 'N/A')}
              </span>
            </InfoRow>
          </SectionPanel>

          {/* HR Integration */}
          <SectionPanel
            icon={<MdGroup size={14} />}
            title="HR Integration"
            accentColor="tw-border-l-violet-400"
            headerBg="tw-bg-violet-50"
            iconColor="tw-text-violet-500"
          >
            <InfoRow label="Employee code">
              {hr.employee_code ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-violet-50 tw-text-violet-700 tw-text-xs tw-font-bold tw-font-mono tw-border tw-border-violet-100">
                  {hr.employee_code}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Timekeeping code"><Val>{safe(hr.timekeeping_code)}</Val></InfoRow>
            <InfoRow label="Date of signing">
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-primary-600">
                <MdCalendarToday size={12} className="tw-shrink-0" />
                {formatDate(hr.date_of_signing) || <Val>{safe(hr.date_of_signing)}</Val>}
              </span>
            </InfoRow>
            <InfoRow label="Direct manager">
              {hr.direct_manager ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-semibold">
                  {hr.direct_manager}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Dept. code"><Val>{safe(hr.department_code)}</Val></InfoRow>
            <InfoRow label="Team code"><Val>{safe(hr.team_code)}</Val></InfoRow>
            <InfoRow label="Team name"><Val>{safe(hr.team_name)}</Val></InfoRow>
            <InfoRow label="Contract type">
              <div className="tw-flex tw-flex-col tw-gap-0.5">
                {hr.contract_type_name ? (
                  <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-emerald-50 tw-text-emerald-700 tw-text-xs tw-font-semibold tw-border tw-border-emerald-100 tw-whitespace-normal tw-leading-snug">
                    {hr.contract_type_name}
                  </span>
                ) : <Val />}
                {hr.contract_type_code && (
                  <span className="tw-text-xs tw-text-slate-400 tw-font-mono">{hr.contract_type_code}</span>
                )}
              </div>
            </InfoRow>
            <InfoRow label="Work location">
              {hr.working_location_code ? (
                <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-violet-600">
                  <MdLocationOn size={13} className="tw-shrink-0" />
                  {hr.working_location_code}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Company name">
              {hr.company_name ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-slate-700">
                  <MdBusinessCenter size={13} className="tw-text-slate-400 tw-shrink-0" />
                  {hr.company_name} ({hr.company_code})
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Nationality"><Val>{safe(hr.nationality)}</Val></InfoRow>
            <InfoRow label="Worker">
              <ToggleSwitch
                value={!!hr.worker}
                onChange={() => {}}
                disabled
                label={hr.worker ? 'Worker' : 'Staff'}
              />
            </InfoRow>
          </SectionPanel>

        </div>
      </Card>
    </>
  );
};

EmployeeDetailInfo.propTypes = {
  employee: PropTypes.object,
};

export default EmployeeDetailInfo;
